import { createClient } from "@connectrpc/connect";
import { grpcTransportProvider } from "../grpc/GrpcTransport";
import { mapConnectError } from "../grpc/mapConnectError";
import { StorageService as StorageGrpcService } from "../../../generated/grpc/storage_pb";
import { SasUploadUrlResponse } from "../../response/storage/SasUploadUrlResponse";

/** Servicio de subida de archivos al storage: pide un SAS de escritura para un
 *  nombre de archivo y sube el archivo directo al blob. El SAS se pide por
 *  gRPC-Web (`StorageService.GetSasUploadUrl` — antes `GET storage/sas-url`,
 *  ver CLAUDE.md del Server, sección migración a gRPC-Web); la subida en sí
 *  sigue siendo un PUT directo a Azure Blob (`upload`, más abajo), que nunca
 *  pasó por nuestra API. */
export class StorageService {
  /** Pide la URL con SAS de escritura para `fileName` (y la URL final del blob). */
  private async getSasUrl(fileName: string): Promise<SasUploadUrlResponse> {
    const client = createClient(StorageGrpcService, grpcTransportProvider.get());

    try {
      const response = await client.getSasUploadUrl({ fileName });
      return { sasUrl: response.sasUrl, blobUrl: response.blobUrl };
    } catch (error) {
      throw mapConnectError(error);
    }
  }

  /** Sube el archivo al `sasUrl` (PUT de blob en bloque) y devuelve el `blobUrl`
   *  **limpio** (sin SAS token) — la ruta final del blob que consume el server
   *  (p. ej. `POST /fertilizacion/parse`); lanza si falla. */
  async upload(file: File): Promise<string> {
    const sas = await this.getSasUrl(file.name);
    const response = await fetch(sas.sasUrl, {
      method: "PUT",
      headers: {
        "x-ms-blob-type": "BlockBlob",
      },
      body: file,
    });
    if (!response.ok) {
      throw new Error(`Error al subir el archivo: ${response.status}`);
    }
    return sas.blobUrl;
  }
}
